import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { color } from '@/colors';
import { CheckMarkCircleIcon } from '@/UI/Icons';
import { I18N_KEYS } from '@/language/keys';
import { log } from '@/logger/logger';
import { useGlobalStore } from '@/state/global.state';
import { unpublishToolFromCommunity } from '@/api/workflowCommunityPublishing.api';
import { WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES } from '@/consts/analytics.consts';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import { invalidateToolListingStatus } from '@/hooks/useToolListingStatus';
import { invalidateToolCommunityMetadata } from './useWorkflowCommunityMetadata';
import {
  getCommunityPublishErrorPayload,
  type WorkflowCommunityPublishTrackingContext,
} from './workflowCommunityPublishAnalytics';

const logger = log.getLogger('useUnpublishToolFromCommunity');

const UNPUBLISH = I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.UNPUBLISH;

/** Unpublishes a tool (`weave_app`) from Community, keyed by `weave_app_id`. Mirrors {@link useUnpublishWorkflowFromCommunity}. */
export const useUnpublishToolFromCommunity = (
  weaveAppId: string,
  trackingContext?: WorkflowCommunityPublishTrackingContext,
  orgName?: string,
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const updateSnackbarData = useGlobalStore((s) => s.updateSnackbarData);
  const { track } = useAnalytics();

  const trackingPayload = useMemo(
    () =>
      trackingContext
        ? {
            ...trackingContext,
            workflowId: trackingContext.workflowId ?? weaveAppId,
          }
        : undefined,
    [trackingContext, weaveAppId],
  );

  return useCallback(async () => {
    try {
      await unpublishToolFromCommunity(weaveAppId);
    } catch (error) {
      if (trackingPayload) {
        track(
          WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.UNPUBLISH_RESULT,
          {
            ...trackingPayload,
            result: 'failure',
            ...getCommunityPublishErrorPayload(error),
          },
          TrackTypeEnum.Product,
        );
      }
      logger.error('Failed to unpublish tool from community', error, {
        weaveAppId,
        status: isAxiosError(error) ? error.response?.status : undefined,
      });
      updateSnackbarData({ text: t(UNPUBLISH.FAILURE_SNACKBAR), isOpen: true });
      return;
    }

    try {
      await Promise.all([
        invalidateToolCommunityMetadata(queryClient, weaveAppId),
        invalidateToolListingStatus(queryClient, weaveAppId),
      ]);
    } catch (refetchError) {
      logger.error('Failed to refetch metadata after unpublish', refetchError, {
        weaveAppId,
        status: isAxiosError(refetchError) ? refetchError.response?.status : undefined,
      });
    }

    if (trackingPayload) {
      track(
        WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.UNPUBLISH_RESULT,
        {
          ...trackingPayload,
          result: 'success',
        },
        TrackTypeEnum.Product,
      );
    }

    updateSnackbarData({
      text: orgName ? t(UNPUBLISH.SUCCESS_SNACKBAR_TOOL_ORG, { orgName }) : t(UNPUBLISH.SUCCESS_SNACKBAR_TOOL),
      icon: <CheckMarkCircleIcon width={16} height={16} style={{ color: color.Black100 }} />,
      isOpen: true,
    });
  }, [weaveAppId, queryClient, updateSnackbarData, t, track, trackingPayload, orgName]);
};
